import { UserNotificationPatchRequest, NotificationStatus, UserNotificationSearchResponse } from '@lio/api-types';
import { UserNotification } from '@models/notification';
import { useQueryClient, useMutation, InfiniteData } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { NotificationKeys } from '@services/notification/keys';
export const useUpdateUserNotificationStatus = () => {
  const queryClient = useQueryClient();
  const client = useClient();
  const mutationFn = (request: UserNotificationPatchRequest) => {
    const { userId, status, notificationIds } = request;
    const url = `user/${userId}/notification`;
    return client.patch(url, {
      json: {
        status,
        notificationIds,
      },
    });
  };
  const notificationsQueryKey = NotificationKeys.searchUserNotifications({ pageLimit: 10 });
  const result = useMutation({
    mutationFn,
    onMutate: async (updatedNotification) => {
      const { status, notificationIds } = updatedNotification;
      const attributeName: Record<NotificationStatus, string> = {
        seen: 'seenAt',
        read: 'readAt',
        dismissed: 'dismissedAt',
      };
      const fillAttributeByStatus = (item: UserNotification, status: NotificationStatus) => {
        if (notificationIds?.includes(item?.id as string)) {
          return {
            ...item,
            [attributeName[status]]: new Date(),
          };
        }
        return item;
      };
      await queryClient.cancelQueries({ queryKey: notificationsQueryKey });
      const previousNotifications =
        queryClient.getQueryData<InfiniteData<UserNotificationSearchResponse>>(notificationsQueryKey);
      queryClient.setQueryData<InfiniteData<UserNotificationSearchResponse>>(notificationsQueryKey, (prevState) => {
        if (!prevState) return undefined;
        const { pageParams, pages } = prevState;
        const newPages = pages.map((p) => ({
          pagination: p.pagination,
          items: p.items
            ?.map((i) => fillAttributeByStatus(i, status as NotificationStatus))
            .filter((n) => !n?.dismissedAt),
        }));
        return {
          pageParams,
          pages: newPages,
        };
      });
      return { previousNotifications };
    },
    onError: (err, newTodo, context) => {
      queryClient.setQueryData(notificationsQueryKey, context?.previousNotifications);
      queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NotificationKeys.summary });
      queryClient.invalidateQueries({ queryKey: NotificationKeys.userNotifications });
    },
  });
  return { ...result, mutateNotificationStatus: result.mutate };
};
